// ── Types ──

export interface ExtractedItem {
  category?: string
  subcategory?: string
  brand?: string
  model?: string
  color_primary?: string
  color_secondary?: string
  material?: string
  size?: string
  condition?: string
  distinguishing_marks?: string
  serial_number?: string
  venue_name?: string
  venue_type?: string
  venue_city?: string
  venue_state?: string
  specific_location?: string
  date_occurred?: string
  time_approximate?: string
  reporter_name?: string
  reporter_phone?: string
  reporter_email?: string
  notes?: string
  /** Owner-view only: concatenated user transcript turns. Returned by
   *  the bearer-code-gated GET /api/reports/:code endpoint. */
  voice_transcript?: string
  photo_urls?: string[]
  confidence?: Record<string, number>
  venue_alternatives?: VenueAlternative[]
  [key: string]: string | number | string[] | VenueAlternative[] | Record<string, number> | undefined
}

export interface VenueAlternative {
  place_id: string
  name: string
  address: string | null
  city: string | null
  state: string | null
}

export interface VoiceResponse {
  session_id: string
  item_id: string
  transcript: string
  response: string
  extracted_item: Partial<ExtractedItem>
  is_complete: boolean
  reference_code?: string
}

export interface ItemReport {
  reference_code: string
  report_type: 'lost' | 'found'
  status: string
  item: ExtractedItem & { id?: string }
  created_at: string
}

// ── API Functions ──

export async function sendVoiceMessage(
  audio: Blob,
  sessionId?: string,
  reportType: 'lost' | 'found' = 'lost'
): Promise<VoiceResponse> {
  const form = new FormData()
  form.append('audio', audio, 'recording.webm')
  if (sessionId) form.append('session_id', sessionId)
  form.append('report_type', reportType)

  const res = await fetch('/api/voice', {
    method: 'POST',
    body: form,
  })

  if (!res.ok) {
    throw new Error(`Voice API error: ${res.status}`)
  }

  return res.json()
}

export async function sendTextMessage(
  text: string,
  sessionId?: string,
  reportType: 'lost' | 'found' = 'lost'
): Promise<VoiceResponse> {
  const res = await fetch('/api/voice', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text,
      session_id: sessionId,
      report_type: reportType,
    }),
  })

  if (!res.ok) {
    throw new Error(`Voice API error: ${res.status}`)
  }

  return res.json()
}

export async function finalizeSession(sessionId: string): Promise<{
  reference_code: string
  item_id: string
  status: string
}> {
  const res = await fetch(`/api/voice/finalize/${sessionId}`, { method: 'POST' })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`Finalize failed: ${res.status} ${body}`)
  }
  return res.json()
}

export async function getReport(referenceCode: string): Promise<ItemReport> {
  const res = await fetch(`/api/reports/${referenceCode}`)

  if (!res.ok) {
    throw new Error(`Report API error: ${res.status}`)
  }

  const data = await res.json()
  // API returns { item: { ...dbRow } }. Drizzle returns camelCase keys; the
  // frontend's ItemReport type uses snake_case, so reshape per-field. Reading
  // snake_case off `row` directly silently returns undefined — caused the
  // "Found item report" label bug on lost reports (report_type undefined,
  // ternary fell through). Keep this list in sync with `items` schema.
  const row = data.item
  return {
    reference_code: row.referenceCode,
    report_type: row.reportType,
    status: row.status,
    created_at: row.createdAt,
    item: {
      id: row.id,
      category: row.category,
      subcategory: row.subcategory,
      brand: row.brand,
      model: row.model,
      color_primary: row.colorPrimary,
      color_secondary: row.colorSecondary,
      material: row.material,
      size: row.size,
      condition: row.condition,
      distinguishing_marks: row.distinguishingMarks,
      serial_number: row.serialNumber,
      venue_name: row.venueName,
      venue_city: row.venueCity,
      venue_state: row.venueState,
      venue_type: row.venueType,
      specific_location: row.specificLocation,
      date_occurred: row.dateOccurred,
      time_approximate: row.timeApproximate,
      reporter_name: row.reporterName,
      reporter_phone: row.reporterPhone,
      reporter_email: row.reporterEmail,
      photo_urls: row.photoUrls,
      confidence: row.confidence,
      notes: row.notes,
      voice_transcript: row.voiceTranscript,
    },
  }
}

// ── Edit / append details to an existing report ──
//
// Reference code is the bearer auth — only the reporter has it.
// `additional_notes` appends to the notes field (with a dated separator);
// any other listed field replaces. Server allowlists; status/contact are
// excluded (status is server-managed, contact uses subscribeToAlerts).

export type ReportPatch = {
  category?: string
  subcategory?: string
  brand?: string
  model?: string
  color_primary?: string
  color_secondary?: string
  material?: string
  size?: string
  distinguishing_marks?: string
  serial_number?: string
  venue_name?: string
  venue_type?: string
  specific_location?: string
  date_occurred?: string
  time_approximate?: string
  notes?: string
  additional_notes?: string
}

export async function updateReport(
  referenceCode: string,
  patch: ReportPatch
): Promise<{ ok: boolean; reference_code: string }> {
  const res = await fetch(`/api/reports/${encodeURIComponent(referenceCode)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw new Error(`Update failed: ${res.status} ${body}`)
  }
  return res.json()
}

// ── Public stats (round-11 trust signal — small live counter for the
//    landing page; per critic P2: "a live counter is the cheapest real
//    third-party trust signal — if the number is real and ≥ 50, +0.5
//    to Trust"). Cached and small to keep the landing fast.

export interface PublicStats {
  active_items: number
  returned_items: number
}

export async function getPublicStats(): Promise<PublicStats | null> {
  try {
    const res = await fetch('/api/reports/_stats')
    if (!res.ok) return null
    return (await res.json()) as PublicStats
  } catch {
    return null
  }
}

// ── Real-time alerts subscription ──

export async function subscribeToAlerts(
  referenceCode: string,
  contact: { phone?: string; email?: string },
  options?: { sendCode?: boolean }
): Promise<{ ok: boolean }> {
  // sendCode=true asks the server to also email the reference code
  // immediately (email-as-credential flow shipped in round 10). Default
  // false preserves the existing alerts-subscription behavior.
  const res = await fetch(`/api/reports/${encodeURIComponent(referenceCode)}/notify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...contact,
      send_code: options?.sendCode ? true : undefined,
    }),
  })
  if (!res.ok) throw new Error(`Subscribe failed: ${res.status}`)
  return res.json()
}

// ── Venue search ──

export async function searchVenues(query: string): Promise<VenueAlternative[]> {
  const q = query.trim()
  if (q.length < 2) return []
  try {
    const res = await fetch(`/api/venues/search?q=${encodeURIComponent(q)}`)
    if (!res.ok) return []
    const data = await res.json()
    return (data.candidates ?? []) as VenueAlternative[]
  } catch {
    return []
  }
}

// ── Matches ──

export interface MatchCandidate {
  match_id: string
  score: number
  tier: 'high' | 'medium' | 'low' | 'none'
  status: string
  signals: Record<string, { signal: number; weight: number; detail: string }>
  opposite_item: {
    id: string
    reference_code: string
    report_type: 'lost' | 'found'
    category: string | null
    subcategory: string | null
    brand: string | null
    model: string | null
    color_primary: string | null
    venue_name: string | null
    date_occurred: string | null
    status: string
    created_at: string
  }
  pickup_code: string | null
  created_at: string
}

export async function getMatches(itemIdOrRef: string): Promise<MatchCandidate[]> {
  const res = await fetch(`/api/matches/${itemIdOrRef}`)
  if (!res.ok) return []
  const data = await res.json()
  return data.matches ?? []
}

// ── TTS (Neural via OpenAI tts-1 with Web Speech API fallback) ──

let currentAudio: HTMLAudioElement | null = null
let warmedAudio: HTMLAudioElement | null = null
// Generation token for the in-flight speak() request. stopSpeaking()
// bumps it, and any speak() awaiting fetch/blob/play with an older
// generation aborts before audio.play(). Fixes the bug where tapping
// the mic to start recording while TTS was loading would let the agent
// audio start playing on top of the user's recording.
let speakGen = 0

// Tiny silent MP3 (1 frame) — triggers iOS audio session authorization
const SILENT_MP3 = 'data:audio/mpeg;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7v////////////////////////////////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAABhgC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7v////////////////////////////////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA'

/**
 * Warm up audio from a user gesture (tap/click).
 * Creates a pre-authorized Audio element that can play later on iOS.
 * Must be called during a user gesture handler (onClick, onTouchStart).
 */
export function warmUpTTS() {
  if (warmedAudio) return
  const audio = new Audio(SILENT_MP3)
  audio.volume = 0.01
  audio.play().then(() => {
    audio.pause()
    audio.volume = 0.9
    warmedAudio = audio
  }).catch(() => {
    // Silent fail — Web Speech API fallback will be used
  })
}

/**
 * Speak text using neural TTS (OpenAI tts-1 "nova").
 * Falls back to browser SpeechSynthesis if the API is unavailable.
 *
 * iOS Safari requires audio playback to start during a user gesture.
 * We create the Audio element and call play() with a silent data URI
 * during warmUpTTS() (called on tap), then swap in the real audio src.
 */
export async function speak(text: string) {
  stopSpeaking()
  const myGen = ++speakGen

  try {
    const res = await fetch('/api/tts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    })

    // If stopSpeaking() ran while we were awaiting the response, abort.
    if (myGen !== speakGen) return

    if (res.ok) {
      const blob = await res.blob()
      if (myGen !== speakGen) return

      const url = URL.createObjectURL(blob)

      // If warmup created a pre-authorized audio element, reuse it
      if (warmedAudio) {
        currentAudio = warmedAudio
        warmedAudio = null
        currentAudio.src = url
      } else {
        currentAudio = new Audio(url)
      }

      currentAudio.volume = 0.9
      currentAudio.onended = () => {
        URL.revokeObjectURL(url)
        currentAudio = null
      }
      // Final check before play() — stopSpeaking() may have fired during
      // the audio-element setup synchronously (e.g. immediate mic-tap).
      if (myGen !== speakGen) return
      await currentAudio.play()
      return
    }
  } catch {
    // Fall through to Web Speech API
  }

  // Fallback: browser SpeechSynthesis
  if (myGen !== speakGen) return
  if ('speechSynthesis' in window) {
    speechSynthesis.cancel()
    const utterance = new SpeechSynthesisUtterance(text)
    utterance.rate = 1.0
    utterance.pitch = 1.0
    utterance.volume = 0.9
    speechSynthesis.speak(utterance)
  }
}

export function stopSpeaking() {
  // Bump the generation so any pending speak() aborts before .play().
  speakGen += 1
  if (currentAudio) {
    currentAudio.pause()
    currentAudio = null
  }
  if ('speechSynthesis' in window) {
    speechSynthesis.cancel()
  }
}
